import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, Flame, MessageSquareText, Users, Target, CheckCircle2,
  ArrowRight, Sparkles, Globe, Trophy,
} from 'lucide-react';
import { useLang } from '@/contexts/LangContext';
import { useTheme } from '@/contexts/ThemeContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import DownloadAppBanner from '@/components/DownloadAppBanner';
import BusinessRedirectRibbon from '@/components/BusinessRedirectRibbon';

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } },
};

// Fixed top header with brand + language switcher + login button.
function Header({ onLogin, loginLabel = 'Log in' }) {
  const { resolvedTheme } = useTheme();
  const brandSrc = resolvedTheme === 'dark' ? '/becaim-wordmark-dark.png' : '/becaim-wordmark.png';
  return (
    <header
      className="fixed top-0 inset-x-0 z-50 bg-white/90 dark:bg-[#0F172A]/90 backdrop-blur-md border-b-2 border-[#E2E8F0] dark:border-[#1E293B]"
      data-testid="landing-header"
    >
      {/* iter-115 — top-of-page ribbon redirecting business visitors to
          business.becaim.app. Sits INSIDE the fixed header so both stay
          pinned at top-0 without positioning gymnastics. When the ribbon
          is dismissed the header collapses back to its 64px height,
          leaving the hero's pt-28 intact. */}
      <BusinessRedirectRibbon />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-2">
        <img
          src={brandSrc}
          alt="BecAim AI"
          className="h-7 sm:h-8 w-auto select-none"
          draggable="false"
          data-testid="landing-brand"
        />
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            onClick={onLogin}
            className="font-extrabold text-sm bg-white text-[#1CB0F6] border-2 border-[#E2E8F0] border-b-4 rounded-2xl py-2 px-5 active:border-b-2 active:translate-y-[2px] hover:bg-[#F7F9FC] transition-all"
            data-testid="header-login-btn"
          >
            {loginLabel}
          </button>
        </div>
      </div>
    </header>
  );
}

// 8 founder/creator/career examples × 3 languages. Each language array MUST
// be the same length (pair-rotation logic assumes this).
const SHADE_TEMPLATES = [
  { chipColor: '#4F46E5', chipBg: '#EEF2FF', streakIconColor: '#FF8E2C', streakIconBg: 'rgba(255,142,44,0.15)' },
  { chipColor: '#E11D48', chipBg: '#FFE4E6', streakIconColor: '#E11D48', streakIconBg: 'rgba(225,29,72,0.15)' },
  { chipColor: '#0EA5A4', chipBg: '#CCFBF1', streakIconColor: '#0EA5A4', streakIconBg: 'rgba(14,165,164,0.15)' },
  { chipColor: '#7E5BEF', chipBg: '#EDE4FF', streakIconColor: '#7E5BEF', streakIconBg: 'rgba(126,91,239,0.15)' },
  { chipColor: '#D97706', chipBg: '#FEF3C7', streakIconColor: '#D97706', streakIconBg: 'rgba(217,119,6,0.15)' },
  { chipColor: '#DC2626', chipBg: '#FEE2E2', streakIconColor: '#DC2626', streakIconBg: 'rgba(220,38,38,0.15)' },
  { chipColor: '#1899D6', chipBg: '#E0F2FE', streakIconColor: '#1899D6', streakIconBg: 'rgba(24,153,214,0.15)' },
  { chipColor: '#16A34A', chipBg: '#DCFCE7', streakIconColor: '#16A34A', streakIconBg: 'rgba(22,163,74,0.15)' },
];

const PROGRESS = [
  { pct: 62, day: 7,  streak: 6  },
  { pct: 78, day: 11, streak: 10 },
  { pct: 40, day: 5,  streak: 4  },
  { pct: 71, day: 9,  streak: 8  },
  { pct: 33, day: 3,  streak: 2  },
  { pct: 84, day: 14, streak: 13 },
  { pct: 55, day: 6,  streak: 5  },
  { pct: 67, day: 12, streak: 11 },
];

const TEXTS_BY_LANG = {
  en: {
    streak: (d, s) => `Day ${d} · ${s}-day streak`,
    todays_mission: "Today's Mission",
    progress_to: (pct) => `${pct}% to weekly target`,
    mark_complete: 'Mark complete',
    examples: [
      { chip: 'I want to Grow my business',          mission: 'Send 5 personalised cold-outreach emails today.' },
      { chip: 'I want to become an Influencer',      mission: 'Post 1 reel and reply to your last 20 comments.' },
      { chip: 'I want to raise my first round',      mission: 'Draft your investor one-pager — finish the problem slide today.' },
      { chip: 'I want to ship my side project',      mission: 'Build the signup form and deploy to staging by tonight.' },
      { chip: 'I want to land a new job',            mission: 'Apply to 3 senior roles and DM 2 hiring managers today.' },
      { chip: 'I want to grow my YouTube channel',   mission: 'Script and shoot one short — publish before 7pm.' },
      { chip: 'I want to write my first book',       mission: 'Write 500 words on chapter 3 — no editing.' },
      { chip: 'I want to learn to code',             mission: 'Finish today\u2019s LeetCode easy and explain it out loud.' },
    ],
  },
  hi: {
    streak: (d, s) => `दिन ${d} · ${s}-दिन की लकीर`,
    todays_mission: 'आज का मिशन',
    progress_to: (pct) => `साप्ताहिक लक्ष्य का ${pct}%`,
    mark_complete: 'पूरा करें',
    examples: [
      { chip: 'मैं अपना बिज़नेस बढ़ाना चाहता हूँ',         mission: 'आज 5 ख़ास कोल्ड-आउटरीच ईमेल भेजें।' },
      { chip: 'मैं इन्फ़्लुएंसर बनना चाहता हूँ',           mission: '1 रील पोस्ट करें और पिछले 20 कमेंट्स का जवाब दें।' },
      { chip: 'मैं अपना पहला फ़ंडिंग राउंड उठाना चाहता हूँ', mission: 'अपना इन्वेस्टर वन-पेजर बनाएँ — आज प्रॉब्लम स्लाइड पूरी करें।' },
      { chip: 'मैं अपना साइड प्रोजेक्ट लॉन्च करना चाहता हूँ', mission: 'साइन-अप फ़ॉर्म बनाएँ और रात तक स्टेजिंग पर डिप्लॉय करें।' },
      { chip: 'मैं नई नौकरी पाना चाहता हूँ',              mission: 'आज 3 सीनियर रोल्स में अप्लाई करें, 2 हायरिंग मैनेजर्स को DM करें।' },
      { chip: 'मैं YouTube चैनल बढ़ाना चाहता हूँ',          mission: 'एक शॉर्ट का स्क्रिप्ट + शूट करें और शाम 7 बजे से पहले पब्लिश करें।' },
      { chip: 'मैं अपनी पहली किताब लिखना चाहता हूँ',         mission: 'चैप्टर 3 पर 500 शब्द लिखें — एडिट मत करें।' },
      { chip: 'मैं कोडिंग सीखना चाहता हूँ',                mission: 'आज की LeetCode easy हल करें और ज़ोर से समझाएँ।' },
    ],
  },
  te: {
    streak: (d, s) => `రోజు ${d} · ${s}-రోజుల వరుస`,
    todays_mission: 'నేటి మిషన్',
    progress_to: (pct) => `వారపు లక్ష్యానికి ${pct}%`,
    mark_complete: 'పూర్తి చేయండి',
    examples: [
      { chip: 'నేను నా బిజినెస్ పెంచాలనుకుంటున్నాను',         mission: 'ఈ రోజు 5 వ్యక్తిగతీకరించిన కోల్డ్ ఈమెయిల్స్ పంపండి.' },
      { chip: 'నేను ఇన్‌ఫ్లుయెన్సర్ కావాలనుకుంటున్నాను',       mission: '1 రీల్ పోస్ట్ చేసి, చివరి 20 కామెంట్స్‌కు బదులివ్వండి.' },
      { chip: 'నేను తొలి ఫండింగ్ రౌండ్ తేవాలనుకుంటున్నాను',   mission: 'మీ ఇన్వెస్టర్ వన్-పేజర్ తయారు చేయండి — ఈరోజు ప్రాబ్లం స్లైడ్ పూర్తి చేయండి.' },
      { chip: 'నేను నా సైడ్ ప్రాజెక్ట్ షిప్ చేయాలనుకుంటున్నాను', mission: 'సైన్అప్ ఫారం బిల్డ్ చేసి ఈరాత్రి స్టేజింగ్‌కి డిప్లాయ్ చేయండి.' },
      { chip: 'నేను కొత్త ఉద్యోగం పొందాలనుకుంటున్నాను',        mission: '3 సీనియర్ రోల్స్‌కు అప్లై చేసి, 2 హైరింగ్ మేనేజర్‌లకు DM చేయండి.' },
      { chip: 'నేను నా YouTube ఛానల్ పెంచాలనుకుంటున్నాను',     mission: 'ఒక షార్ట్ స్క్రిప్ట్ + షూట్ చేసి సాయంత్రం 7 లోపు పబ్లిష్ చేయండి.' },
      { chip: 'నేను నా మొదటి పుస్తకం రాయాలనుకుంటున్నాను',       mission: 'చాప్టర్ 3 పై 500 పదాలు రాయండి — ఎడిట్ చేయవద్దు.' },
      { chip: 'నేను కోడింగ్ నేర్చుకోవాలనుకుంటున్నాను',           mission: 'ఈరోజు LeetCode easy పూర్తి చేసి బిగ్గరగా వివరించండి.' },
    ],
  },
};

// Two slots — top card rotates +2°, bottom card rotates -2°.
const ROTATIONS = [2, -2];
const ROTATE_MS = 4500;     // how long each pair stays visible
const PAIR_STEP = 2;        // advance by 2 examples each cycle

function MissionMockCard() {
  const { lang } = useLang();
  const [pairStart, setPairStart] = useState(0);

  // Build localized examples from the language map + static shade/progress.
  const examples = useMemo(() => {
    const lex = TEXTS_BY_LANG[lang] || TEXTS_BY_LANG.en;
    return lex.examples.map((ex, i) => ({
      ...ex,
      ...SHADE_TEMPLATES[i % SHADE_TEMPLATES.length],
      progressPct: PROGRESS[i].pct,
      streakLabel: lex.streak(PROGRESS[i].day, PROGRESS[i].streak),
      progressLabel: lex.progress_to(PROGRESS[i].pct),
      todaysMissionLabel: lex.todays_mission,
      markCompleteLabel: lex.mark_complete,
    }));
  }, [lang]);

  // Reset to the start when language flips so users see the first pair first.
  useEffect(() => { setPairStart(0); }, [lang]);

  useEffect(() => {
    const id = setInterval(() => {
      setPairStart((p) => (p + PAIR_STEP) % examples.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [examples.length]);

  const pair = [
    examples[pairStart % examples.length],
    examples[(pairStart + 1) % examples.length],
  ];

  return (
    <motion.div
      animate={{ y: [-6, 6, -6] }}
      transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
      className="relative max-w-sm mx-auto lg:mx-0 space-y-4"
      data-testid="hero-mock-card"
    >
      <div className="absolute -top-4 -left-4 w-20 h-20 rounded-full bg-[#FFC800]/40 blur-2xl" />
      <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-[#1CB0F6]/30 blur-2xl" />
      {pair.map((ex, idx) => (
        <AnimatePresence key={idx} mode="wait">
          <motion.div
            key={ex.chip + pairStart + lang}
            initial={{ opacity: 0, y: 20, scale: 0.95, rotate: ROTATIONS[idx] }}
            animate={{ opacity: 1, y: 0, scale: 1, rotate: ROTATIONS[idx] }}
            exit={{ opacity: 0, y: -10, scale: 0.96 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
            className="relative bg-white rounded-3xl border-2 border-[#E2E8F0] border-b-[6px] p-5"
            data-testid={`hero-mock-example-${idx}`}
          >
            <div
              className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full font-extrabold uppercase tracking-wider mb-3"
              style={{ background: ex.chipBg, color: ex.chipColor }}
            >
              <Target size={11} strokeWidth={3} /> {ex.chip}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: ex.streakIconBg }}
              >
                <Flame size={20} strokeWidth={3} color={ex.streakIconColor} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-extrabold text-[#94A3B8] uppercase tracking-wider">{ex.streakLabel}</p>
                <p className="font-extrabold text-[#4B4B4B] text-sm">{ex.todaysMissionLabel}</p>
              </div>
            </div>
            <p className="font-extrabold text-base text-[#4B4B4B] leading-snug">
              {ex.mission}
            </p>
            <div className="mt-3 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
              <motion.div
                initial={{ width: '0%' }}
                animate={{ width: `${ex.progressPct}%` }}
                transition={{ duration: 1.4, ease: 'easeOut', delay: 0.25 + idx * 0.15 }}
                className="h-full bg-[#58CC02] rounded-full"
              />
            </div>
            <p className="text-[10px] font-extrabold text-[#94A3B8] mt-1">{ex.progressLabel}</p>
            <button
              className="mt-4 w-full bg-[#58CC02] text-white font-extrabold text-sm py-3 rounded-2xl border-b-4 border-[#58A700] flex items-center justify-center gap-2"
              disabled
            >
              <CheckCircle2 size={18} strokeWidth={3} /> {ex.markCompleteLabel}
            </button>
          </motion.div>
        </AnimatePresence>
      ))}

      {/* Dots indicator — shows progress through the pool */}
      <div className="flex justify-center gap-1.5 pt-1" aria-hidden="true">
        {Array.from({ length: Math.ceil(examples.length / PAIR_STEP) }).map((_, i) => {
          const active = i === Math.floor(pairStart / PAIR_STEP);
          return (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                active ? 'w-6 bg-[#1CB0F6]' : 'w-1.5 bg-[#CBD5E1]'
              }`}
            />
          );
        })}
      </div>
    </motion.div>
  );
}

// ─── Page-level localized copy (3 languages) ─────────────────────────────
const LANDING_COPY = {
  en: {
    nowLaunch: 'Now in launch mode · free to join',
    h1Part1: 'Level up your life,',
    h1Highlight: 'one mission',
    h1Part2: 'at a time.',
    sub: 'BecAim is the AI mentor that turns your daily goals into rewarding streaks — and lets your friends keep you honest.',
    startFree: 'Start free',
    signIn: 'Sign in',
    poweredBy: 'Powered by Claude',
    memory: 'Mentor with memory',
    languages: '12+ languages',
    pillarsH2: 'Built to make showing up easier.',
    pillarsSub: 'Not another todo app — BecAim is an active mentor that adapts to who you are and how you show up.',
    pillars: [
      { title: 'Adaptive AI Missions',     desc: 'Missions that evolve with your progress — Claude reads your bio, your wins and your skips.' },
      { title: 'Streak Momentum',          desc: 'Daily fire. Lose it once, you feel it.' },
      { title: 'Mentor That Remembers',    desc: 'Your AI mentor remembers past wins, slip-ups and tone — and adjusts.' },
      { title: 'Community Accountability', desc: 'Goals shared with friends or private groups. Showing up is contagious.' },
    ],
    howH2a: 'Three steps.',
    howH2b: 'Zero friction.',
    steps: [
      { title: 'Pick your immediate goal',   desc: 'Tell BecAim what you want to achieve.' },
      { title: 'Get your daily AI mission',  desc: 'Your mentor turns the goal into one actionable step a day.' },
      { title: 'Build streaks with friends', desc: 'Share your wins. Hold each other accountable.' },
    ],
    immediateChip: 'Your immediate goal',
    ctaH2a: 'What\u2019s ONE thing you want to start',
    ctaUnderline: 'this week',
    ctaH2b: '?',
    ctaSub: 'Skip the 5-year dream — pick something specific you can begin tomorrow and finish in 2–4 weeks. Your AI coach turns it into one small daily mission.',
    ctaPlaceholder: 'e.g. Grow my business in 30 days',
    noCard: 'No card. No spam. Cancel anytime.',
    builtFor: '© 2026 BecAim · Built for builders.',
    terms: 'Terms',
    privacy: 'Privacy',
    alreadyLogin: 'Already with us? Log in →',
    login: 'Log in',
  },
  hi: {
    nowLaunch: 'अब लॉन्च मोड में · मुफ़्त जुड़ें',
    h1Part1: 'अपना जीवन ऊपर ले जाएँ,',
    h1Highlight: 'एक मिशन',
    h1Part2: 'एक बार में।',
    sub: 'BecAim वो AI मेंटर है जो आपके रोज़ के लक्ष्यों को इनामी लकीरों में बदलता है — और दोस्तों के साथ आपको ईमानदार रखता है।',
    startFree: 'मुफ़्त शुरू करें',
    signIn: 'साइन इन',
    poweredBy: 'Claude द्वारा संचालित',
    memory: 'याद रखने वाला मेंटर',
    languages: '12+ भाषाएँ',
    pillarsH2: 'रोज़ दिखने को आसान बनाने के लिए बना।',
    pillarsSub: 'एक और टू-डू ऐप नहीं — BecAim एक सक्रिय मेंटर है जो आप कौन हैं और कैसे दिखते हैं इसके मुताबिक़ ढलता है।',
    pillars: [
      { title: 'अनुकूल AI मिशन',          desc: 'आपकी प्रगति के साथ बदलते मिशन — Claude आपकी जीवनी, जीत और छूटी हुई कोशिशें पढ़ता है।' },
      { title: 'लकीर की रफ़्तार',           desc: 'रोज़ की आग। एक बार खोई, तो महसूस होगा।' },
      { title: 'याद रखने वाला मेंटर',        desc: 'आपका AI मेंटर पुरानी जीत, चूक और टोन याद रखता है — और उसी हिसाब से ढलता है।' },
      { title: 'समुदाय की जवाबदेही',         desc: 'दोस्तों या निजी समूह के साथ साझा लक्ष्य। दिखना संक्रामक है।' },
    ],
    howH2a: 'तीन कदम।',
    howH2b: 'शून्य रुकावट।',
    steps: [
      { title: 'अपना तत्काल लक्ष्य चुनें',    desc: 'BecAim को बताएँ कि आप क्या हासिल करना चाहते हैं।' },
      { title: 'रोज़ का AI मिशन पाएँ',       desc: 'आपका मेंटर लक्ष्य को रोज़ के एक छोटे कदम में बदलता है।' },
      { title: 'दोस्तों के साथ लकीर बनाएँ',   desc: 'अपनी जीतें साझा करें। एक-दूसरे को जवाबदेह रखें।' },
    ],
    immediateChip: 'आपका तत्काल लक्ष्य',
    ctaH2a: 'इस हफ़्ते आप शुरू करना चाहते हैं',
    ctaUnderline: 'ऐसी एक चीज़',
    ctaH2b: '?',
    ctaSub: '5 साल का सपना छोड़ें — कुछ ख़ास चुनें जो कल शुरू कर सकें और 2–4 हफ़्तों में पूरा हो। आपका AI कोच इसे रोज़ के एक छोटे मिशन में बदलेगा।',
    ctaPlaceholder: 'जैसे: 30 दिनों में अपना बिज़नेस बढ़ाना',
    noCard: 'कोई कार्ड नहीं। कोई स्पैम नहीं। कभी भी रद्द करें।',
    builtFor: '© 2026 BecAim · बिल्डर्स के लिए बना।',
    terms: 'शर्तें',
    privacy: 'गोपनीयता',
    alreadyLogin: 'पहले से हमारे साथ हैं? लॉग इन →',
    login: 'लॉग इन',
  },
  te: {
    nowLaunch: 'ఇప్పుడు లాంచ్ మోడ్‌లో · ఉచితంగా చేరండి',
    h1Part1: 'మీ జీవితాన్ని పైకి తీసుకెళ్లండి,',
    h1Highlight: 'ఒక మిషన్',
    h1Part2: 'చొప్పున.',
    sub: 'BecAim అనేది మీ రోజువారీ లక్ష్యాలను బహుమతి వరుసలుగా మార్చే AI మెంటర్ — మీ స్నేహితులు మిమ్మల్ని నిజాయితీగా ఉంచుతారు.',
    startFree: 'ఉచితంగా ప్రారంభించండి',
    signIn: 'సైన్ ఇన్',
    poweredBy: 'Claude ద్వారా శక్తినిచ్చింది',
    memory: 'జ్ఞాపకం ఉన్న మెంటర్',
    languages: '12+ భాషలు',
    pillarsH2: 'రోజూ చూపించుకోవడం సులభం చేయడానికి తయారు చేయబడింది.',
    pillarsSub: 'మరో todo యాప్ కాదు — BecAim మీరు ఎవరు, ఎలా చూపిస్తారో దానికి అనుగుణంగా మారే ఒక చురుకైన మెంటర్.',
    pillars: [
      { title: 'అనుకూల AI మిషన్‌లు',       desc: 'మీ ప్రగతితో మారే మిషన్‌లు — Claude మీ బయో, విజయాలు, వదిలేసిన పనులు చదువుతుంది.' },
      { title: 'వరుస ఊపు',                desc: 'రోజువారీ నిప్పు. ఒకసారి కోల్పోతే, మీరు దాన్ని అనుభవిస్తారు.' },
      { title: 'జ్ఞాపకం ఉన్న మెంటర్',       desc: 'మీ AI మెంటర్ గత విజయాలు, తప్పిదాలు, టోన్ గుర్తుపెట్టుకుని తదనుగుణంగా మారుతుంది.' },
      { title: 'కమ్యూనిటీ జవాబుదారీతనం',     desc: 'స్నేహితులు లేదా ప్రైవేట్ గ్రూపులతో పంచుకున్న లక్ష్యాలు. చూపించుకోవడం అంటుకుంటుంది.' },
    ],
    howH2a: 'మూడు దశలు.',
    howH2b: 'శూన్య అడ్డంకి.',
    steps: [
      { title: 'మీ తక్షణ లక్ష్యం ఎంచుకోండి',    desc: 'మీరు ఏం సాధించాలనుకుంటున్నారో BecAim కు చెప్పండి.' },
      { title: 'రోజువారీ AI మిషన్ పొందండి',     desc: 'మీ మెంటర్ లక్ష్యాన్ని రోజుకొక చిన్న చర్యగా మారుస్తుంది.' },
      { title: 'స్నేహితులతో వరుసలు తయారు చేయండి', desc: 'మీ విజయాలు పంచుకోండి. ఒకరికొకరు జవాబుదారీగా ఉండండి.' },
    ],
    immediateChip: 'మీ తక్షణ లక్ష్యం',
    ctaH2a: 'ఈ వారం మీరు మొదలెట్టాలనుకునే',
    ctaUnderline: 'ఒక్క విషయం',
    ctaH2b: ' ఏమిటి?',
    ctaSub: '5-సంవత్సరాల కల వదిలేయండి — రేపే మొదలెట్టి 2–4 వారాల్లో పూర్తయ్యే ఏదైనా నిర్దిష్టమైనది ఎంచుకోండి. మీ AI కోచ్ దాన్ని రోజువారీ చిన్న మిషన్‌గా మారుస్తుంది.',
    ctaPlaceholder: 'ఉదా. 30 రోజుల్లో నా బిజినెస్ పెంచడం',
    noCard: 'కార్డు లేదు. స్పామ్ లేదు. ఎప్పుడైనా రద్దు చేయండి.',
    builtFor: '© 2026 BecAim · బిల్డర్‌ల కోసం తయారు చేయబడింది.',
    terms: 'నిబంధనలు',
    privacy: 'గోప్యత',
    alreadyLogin: 'ఇప్పటికే మాతో ఉన్నారా? లాగిన్ →',
    login: 'లాగిన్',
  },
};

const PILLAR_ICONS_TONES = [
  { Icon: Brain,             tone: '#1CB0F6', big: true },
  { Icon: Flame,             tone: '#FF8E2C' },
  { Icon: MessageSquareText, tone: '#58CC02' },
  { Icon: Users,             tone: '#FF4B4B' },
];

export default function Landing() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const copy = LANDING_COPY[lang] || LANDING_COPY.en;
  const [goal, setGoal] = useState('');

  const goLogin = useCallback(() => navigate('/login'), [navigate]);

  const startWithGoal = useCallback(() => {
    const trimmed = goal.trim();
    if (trimmed) {
      // Hand-off to onboarding: stash the typed goal so step 4 is pre-filled
      // and the user lands on the categories step with momentum.
      localStorage.setItem('pending_goal', trimmed);
    }
    navigate('/login');
  }, [goal, navigate]);

  return (
    <div className="min-h-screen bg-[#F7F9FC] text-[#4B4B4B] overflow-x-hidden gm-safe-top">
      <Header onLogin={goLogin} loginLabel={copy.login} />

      {/* ─── HERO ─────────────────────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-5 sm:px-8" data-testid="landing-hero">
        <div className="max-w-6xl mx-auto">
          {/* iter-109 — phone-web visitors see the store CTA above the hero. */}
          <DownloadAppBanner />
        </div>
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={containerVariants}>
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 bg-[#FFC800]/15 text-[#B58F00] text-[11px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5">
              <Sparkles size={12} strokeWidth={3} /> {copy.nowLaunch}
            </motion.div>
            <motion.h1
              variants={itemVariants}
              className="font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              {copy.h1Part1}{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#1CB0F6]">{copy.h1Highlight}</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#FFC800]/45 -z-0 rounded-sm" />
              </span>{' '}
              {copy.h1Part2}
            </motion.h1>
            <motion.p variants={itemVariants} className="mt-6 text-lg sm:text-xl font-bold text-[#64748B] leading-relaxed">
              {copy.sub}
            </motion.p>
            <motion.div variants={itemVariants} className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                onClick={startWithGoal}
                className="bg-[#1CB0F6] text-white font-extrabold text-base py-3.5 px-7 rounded-2xl border-b-4 border-[#1899D6] active:border-b-0 active:translate-y-1 hover:brightness-110 transition-all flex items-center justify-center gap-2"
                data-testid="hero-start-btn"
              >
                {copy.startFree} <ArrowRight size={18} strokeWidth={3} />
              </button>
              <button
                onClick={goLogin}
                className="bg-white text-[#1CB0F6] font-extrabold text-base py-3.5 px-7 rounded-2xl border-2 border-[#E2E8F0] border-b-4 active:border-b-2 active:translate-y-[2px] hover:bg-[#F7F9FC] transition-all"
                data-testid="hero-signin-btn"
              >
                {copy.signIn}
              </button>
            </motion.div>
          </motion.div>

          <div className="order-first lg:order-last">
            <MissionMockCard />
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-12 max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[#94A3B8] font-extrabold text-[11px] uppercase tracking-widest">
          <span className="inline-flex items-center gap-2"><Brain size={14} strokeWidth={3} /> {copy.poweredBy}</span>
          <span className="inline-flex items-center gap-2"><MessageSquareText size={14} strokeWidth={3} /> {copy.memory}</span>
          <span className="inline-flex items-center gap-2"><Globe size={14} strokeWidth={3} /> {copy.languages}</span>
        </div>
      </section>

      {/* ─── PILLARS (Bento) ──────────────────────────────────────────── */}
      <section className="py-20 px-5 sm:px-8" data-testid="landing-pillars">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            className="font-extrabold text-3xl sm:text-4xl tracking-tight mb-3"
          >
            {copy.pillarsH2}
          </motion.h2>
          <p className="text-base sm:text-lg text-[#64748B] font-bold mb-10">
            {copy.pillarsSub}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {copy.pillars.map((p, i) => {
              const meta = PILLAR_ICONS_TONES[i] || PILLAR_ICONS_TONES[0];
              const Icon = meta.Icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-white rounded-3xl border-2 border-[#E2E8F0] border-b-[6px] p-6 ${
                    meta.big ? 'md:col-span-7 md:row-span-2' : i === 1 ? 'md:col-span-5' : 'md:col-span-6'
                  }`}
                  data-testid={`pillar-${i}`}
                >
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: `${meta.tone}1F` }}
                  >
                    <Icon size={24} strokeWidth={3} color={meta.tone} />
                  </div>
                  <h3 className="font-extrabold text-xl text-[#4B4B4B]">{p.title}</h3>
                  <p className="mt-2 text-[#64748B] font-bold text-base leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────────────── */}
      <section className="py-16 px-5 sm:px-8 bg-white border-y-2 border-[#E2E8F0]" data-testid="landing-how">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-center font-extrabold text-3xl sm:text-4xl tracking-tight mb-12">
            {copy.howH2a} <span className="text-[#1CB0F6]">{copy.howH2b}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {copy.steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
                data-testid={`step-${i + 1}`}
              >
                <div className="w-14 h-14 rounded-full bg-[#FFC800] text-white font-extrabold text-2xl flex items-center justify-center mx-auto mb-4 border-b-4 border-[#E5A800]">
                  {i + 1}
                </div>
                <h3 className="font-extrabold text-lg">{s.title}</h3>
                <p className="text-[#64748B] font-bold text-base mt-2">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GOAL CAPTURE CTA ─────────────────────────────────────────── */}
      <section className="py-20 px-5 sm:px-8" data-testid="landing-goal-cta">
        <div className="max-w-3xl mx-auto bg-[#1CB0F6] rounded-3xl border-b-[6px] border-[#1899D6] p-8 sm:p-10 text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-12 -left-8 w-32 h-32 rounded-full bg-[#FFC800]/30" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/15 text-white text-[10px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
              <Target size={11} strokeWidth={3} /> {copy.immediateChip}
            </div>
            <h2 className="font-extrabold text-3xl sm:text-4xl leading-tight tracking-tight">
              {copy.ctaH2a} <span className="underline decoration-[#FFC800] decoration-4 underline-offset-4">{copy.ctaUnderline}</span>{copy.ctaH2b}
            </h2>
            <p className="mt-3 font-bold text-white/85 text-base sm:text-lg">
              {copy.ctaSub}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && startWithGoal()}
                placeholder={copy.ctaPlaceholder}
                aria-label={copy.immediateChip}
                className="flex-1 rounded-2xl px-5 py-3.5 text-[#4B4B4B] font-bold text-base bg-white border-b-4 border-[#E2E8F0] focus:outline-none focus:border-[#FFC800]"
                data-testid="cta-goal-input"
                maxLength={140}
              />
              <button
                onClick={startWithGoal}
                className="bg-[#FFC800] text-[#4B4B4B] font-extrabold text-base py-3.5 px-6 rounded-2xl border-b-4 border-[#E5A800] active:border-b-0 active:translate-y-1 hover:brightness-105 transition-all flex items-center justify-center gap-2"
                data-testid="cta-submit-btn"
              >
                {copy.startFree} <ArrowRight size={18} strokeWidth={3} />
              </button>
            </div>
            <p className="mt-3 text-white/70 font-extrabold text-[11px] uppercase tracking-wider">
              <Trophy size={11} strokeWidth={3} className="inline mr-1 -mt-0.5" /> {copy.noCard}
            </p>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────────────── */}
      <footer className="py-10 px-5 sm:px-8 border-t-2 border-[#E2E8F0] bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#94A3B8] font-extrabold text-sm">
            {copy.builtFor}
          </p>
          <div className="flex items-center gap-4">
            <a href="/terms" className="text-[#94A3B8] font-extrabold text-xs hover:text-[#1CB0F6]" data-testid="landing-terms-link">{copy.terms}</a>
            <a href="/privacy" className="text-[#94A3B8] font-extrabold text-xs hover:text-[#1CB0F6]" data-testid="landing-privacy-link">{copy.privacy}</a>
            <button
              onClick={goLogin}
              className="text-[#1CB0F6] font-extrabold text-sm hover:underline"
              data-testid="footer-login-btn"
            >
              {copy.alreadyLogin}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
